import { groupBy, omit, sortBy } from 'lodash';

import type { InstructorProfileViewerByShortNameQuery_InstructorsV1Resource_shortName_instructors_courses_elements as Course } from 'bundles/partners-graphql/components/__generated__/InstructorProfileViewerByShortNameQuery';

type GetListOfCoursesGroupedByLanguagesArgs = {
  coursesList: Array<Course>;
  userLocale: string;
};
type LanguageCodeAndCoursesTuple = [string, Array<Course>];

/**
 * Gets a list of courses grouped by primary language codes.
 * The function first sorts the courses by name and then groups them by their primary languages.
 * It separates courses matching the user's locale and groups the remaining courses by language.
 * It then orders the non-user-locale groups by the number of courses in descending order.
 * The final result is an array of tuples where each tuple contains a language code followed
 * by its array of courses, starting with the user's locale.
 *
 * @param {Object} params - The function parameters.
 * @param {Array} params.coursesList - The list of course objects to be sorted and grouped.
 * @param {string} params.userLocale - The user's locale which is used to group the courses accordingly.
 * @returns {Array<LanguageCodeAndCoursesTuple>} - An array of tuples, each containing a language code and an array of sorted courses.
 */
const getListOfCoursesGroupedByLanguages = ({ coursesList, userLocale }: GetListOfCoursesGroupedByLanguagesArgs) => {
  // Removing empty whitespaces before sorting the list of courses
  const sanitizedCoursesList = coursesList.map((course) => ({ ...course, name: course.name.trim() }));
  const sortedCoursesList = sortBy(sanitizedCoursesList, (course) => course.name);
  const groupedCoursesByPrimaryLanguages = groupBy(sortedCoursesList, (course) => course.primaryLanguages[0]);
  const userLocaleCourses = groupedCoursesByPrimaryLanguages[userLocale] || [];
  const otherLocaleCourses = omit(groupedCoursesByPrimaryLanguages, [userLocale]);
  const coursesInOtherLocalesOrderedByLength: Array<LanguageCodeAndCoursesTuple> = sortBy(
    Object.entries(otherLocaleCourses),
    ([, value]) => -value.length
  );
  const userLocaleAndCoursesTuple: LanguageCodeAndCoursesTuple = [userLocale, userLocaleCourses];
  const transformedListOfCoursesGroupedByLocales: Array<LanguageCodeAndCoursesTuple> = [
    userLocaleAndCoursesTuple,
    ...coursesInOtherLocalesOrderedByLength,
  ];

  return transformedListOfCoursesGroupedByLocales;
};

export default getListOfCoursesGroupedByLanguages;
